/**
 * Layout slot pitch per ogni modulo supportato.
 *
 * Coordinate normalizzate (% del pitch, 0..100):
 * - x: 0 = sinistra del campo, 100 = destra
 * - y: 0 = porta avversaria (alto), 100 = porta tua (basso)
 *
 * Slot 0 = portiere (sempre basso al centro).
 * Slot 1+: in ordine line per line (difesa → centrocampo → attacco).
 */

export type SlotPos = { x: number; y: number; label: string };

/** 11 moduli ufficiali Fantacalcio Mantra 2025/26 (fonte: fantacalcio.it). */
export const MODULI_DISPONIBILI = [
  "3-4-1-2",
  "3-4-2-1",
  "3-4-3",
  "3-5-1-1",
  "3-5-2",
  "4-1-4-1",
  "4-2-3-1",
  "4-3-1-2",
  "4-3-3",
  "4-4-1-1",
  "4-4-2",
] as const;

export type Modulo = (typeof MODULI_DISPONIBILI)[number];

/** Andrea 2026-09-08: il badge con la percentuale delle probabili sta
 * SOTTO il cerchio e sul portiere (y=92) finiva a cavallo del bordo
 * campo. Alzare solo lui lo incollava ai difensori, e traslare tutto
 * schiacciava gli attaccanti sul bordo alto: comprimiamo quindi il
 * campo verso l'alto — l'attacco resta quasi dov'era, il portiere sale
 * di ~8 punti e sotto si libera lo spazio per il badge. */
function alza(y: number): number {
  return y * 0.9 + 1.5;
}

function line(y: number, n: number, labels?: string[]): SlotPos[] {
  // n slot equidistanti su una linea orizzontale, padding 12% lati
  const padding = 12;
  const usable = 100 - padding * 2;
  return Array.from({ length: n }, (_, i) => ({
    x: padding + (usable * (i + 0.5)) / n,
    y: alza(y),
    label: labels?.[i] ?? "",
  }));
}

// Il portiere NON passa da alza(): y tarata a mano. Andrea 2026-09-08:
// ora che la percentuale sta sull'icona, sotto resta solo il nome —
// a 91 finiva sulla riga dell'area, quindi 84 anche su desktop.
const POR: SlotPos = { x: 50, y: 84, label: "Por" };

/** Slot singolo (trequartista, punta centrale): stessa trasformazione
 * verticale delle linee, altrimenti restava "basso" rispetto a loro
 * (Andrea 2026-09-08). */
function solo(y: number, label: string): SlotPos {
  return { x: 50, y: alza(y), label };
}

export const MODULO_LAYOUT: Record<Modulo, SlotPos[]> = {
  "4-3-3": [
    POR,
    ...line(70, 4, ["Ds", "Dc", "Dc", "Dd"]),
    ...line(45, 3, ["C", "C", "C"]),
    ...line(18, 3, ["W", "Pc", "W"]),
  ],
  "4-4-2": [
    POR,
    ...line(70, 4, ["Ds", "Dc", "Dc", "Dd"]),
    ...line(45, 4, ["E", "C", "C", "E"]),
    ...line(18, 2, ["Pc", "Pc"]),
  ],
  // Andrea 2026-10-29: nei moduli con un trequartista solo (4-4-1-1,
  // 4-3-1-2, 3-4-1-2) la T sta a 27 e le punte a 11: a 30-32 il voto coi
  // bonus, che su desktop sta sotto il nome, finiva sopra i mediani (48).
  "4-4-1-1": [
    POR,
    ...line(70, 4, ["Ds", "Dc", "Dc", "Dd"]),
    ...line(48, 4, ["E", "C", "C", "E"]),
    solo(27, "T"),
    solo(11, "Pc"),
  ],
  "4-3-1-2": [
    POR,
    ...line(70, 4, ["Ds", "Dc", "Dc", "Dd"]),
    ...line(48, 3, ["C", "C", "C"]),
    solo(27, "T"),
    ...line(11, 2, ["Pc", "Pc"]),
  ],
  "4-2-3-1": [
    POR,
    ...line(70, 4, ["Ds", "Dc", "Dc", "Dd"]),
    ...line(52, 2, ["M", "M"]),
    ...line(32, 3, ["W", "T", "W"]),
    solo(12, "Pc"),
  ],
  // Christian 2026-10-30: quattro righe fra difesa e punta, distanziate
  // uguali — con M a 56 il voto e i bonus del mediano (desktop) finivano
  // sopra ai difensori.
  "4-1-4-1": [
    POR,
    ...line(74, 4, ["Ds", "Dc", "Dc", "Dd"]),
    solo(55, "M"),
    ...line(36, 4, ["E", "C", "C", "E"]),
    solo(13, "Pc"),
  ],
  "3-4-3": [
    POR,
    ...line(70, 3, ["Dc", "Dc", "Dc"]),
    ...line(45, 4, ["E", "C", "C", "E"]),
    ...line(18, 3, ["W", "Pc", "W"]),
  ],
  "3-4-1-2": [
    POR,
    ...line(70, 3, ["Dc", "Dc", "Dc"]),
    ...line(48, 4, ["E", "C", "C", "E"]),
    solo(27, "T"),
    ...line(11, 2, ["Pc", "Pc"]),
  ],
  "3-4-2-1": [
    POR,
    ...line(70, 3, ["Dc", "Dc", "Dc"]),
    ...line(48, 4, ["E", "C", "C", "E"]),
    ...line(28, 2, ["T", "T"]),
    solo(12, "Pc"),
  ],
  "3-5-2": [
    POR,
    ...line(70, 3, ["Dc", "Dc", "Dc"]),
    ...line(45, 5, ["E", "C", "C", "C", "E"]),
    ...line(16, 2, ["Pc", "Pc"]),
  ],
  "3-5-1-1": [
    POR,
    ...line(70, 3, ["Dc", "Dc", "Dc"]),
    ...line(48, 5, ["E", "C", "C", "C", "E"]),
    solo(28, "T"),
    solo(12, "Pc"),
  ],
};

/** Su MOBILE le etichette sotto i cerchi (nome, percentuale, fascia)
 * pesano di più in proporzione: nei moduli col trequartista la sua riga
 * finiva addosso ai centrocampisti. Qui ridistribuiamo le linee di
 * movimento a distanza UNIFORME tra attacco e difesa, così ogni modulo
 * — 3, 4 o 5 linee — sfrutta tutta l'altezza disponibile. Il portiere
 * non si tocca (Andrea 2026-09-08). */
const MOBILE_Y_ATTACCO = 10;
const MOBILE_Y_DIFESA = 66;
/** Su mobile il portiere sale un po': a 91 il suo nome finiva sulla
 * riga dell'area piccola ed era faticoso da leggere (Andrea 2026-09-08).
 * Su desktop resta a 91, dove la percentuale chiude appena sopra la
 * linea di fondo. */
const MOBILE_Y_PORTIERE = 84;

/** Padding laterale per una linea su mobile: più giocatori ci sono,
 * più la linea si allarga verso i bordi. Con 5 in mezzo (3-5-2, 3-5-1-1)
 * il padding del desktop (12% per lato) li faceva toccare. */
function paddingMobile(n: number): number {
  if (n >= 5) return 3;
  if (n === 4) return 7;
  return 12;
}

export function layoutMobile(slots: SlotPos[]): SlotPos[] {
  const yPortiere = Math.max(...slots.map((s) => s.y));
  const righe = [...new Set(slots.filter((s) => s.y !== yPortiere).map((s) => s.y))].sort(
    (a, b) => a - b,
  ); // dall'alto (attacco) al basso (difesa)
  if (righe.length < 2) return slots;
  const passo = (MOBILE_Y_DIFESA - MOBILE_Y_ATTACCO) / (righe.length - 1);
  const nuovaY = new Map<number, number>(righe.map((y, i) => [y, MOBILE_Y_ATTACCO + passo * i]));
  // Riposiziona anche in orizzontale, linea per linea.
  const perRiga = new Map<number, SlotPos[]>();
  for (const s of slots) {
    const arr = perRiga.get(s.y) ?? [];
    arr.push(s);
    perRiga.set(s.y, arr);
  }
  const nuovaX = new Map<SlotPos, number>();
  for (const [, dellaRiga] of perRiga) {
    const n = dellaRiga.length;
    if (n < 4) continue; // 1-3 slot: il padding standard va già bene
    const pad = paddingMobile(n);
    const usable = 100 - pad * 2;
    // mantieni l'ordine sinistra→destra originale
    [...dellaRiga]
      .sort((a, b) => a.x - b.x)
      .forEach((s, i) => nuovaX.set(s, pad + (usable * (i + 0.5)) / n));
  }
  return slots.map((s) => ({
    ...s,
    x: nuovaX.get(s) ?? s.x,
    y: s.y === yPortiere ? MOBILE_Y_PORTIERE : (nuovaY.get(s.y) ?? s.y),
  }));
}

/** Reparto classic di ognuno degli 11 slot del modulo (P, D, D, ..., A).
 * Si basa sui numeri del modulo: es. "4-3-3" -> 1P + 4D + 3C + 3A.
 *
 * Doppio uso: sono le label degli slot in modalita' CLASSIC (dove non
 * esistono i ruoli mantra) e il raggruppamento per reparto nel popup
 * "dove lo schiero?" (Andrea 2026-09-09), che vale in entrambe le
 * modalita'.
 */
export function labelsClassicDaModulo(modulo: string): string[] {
  const parts = modulo.split("-").map(Number);
  if (parts.some((n) => !Number.isFinite(n))) return Array(11).fill("");
  // Slot 0 sempre P. Poi gruppi: prima sezione = D, ultima = A, intermedie = C
  const labels = ["P"];
  for (let i = 0; i < parts.length; i++) {
    const ruolo = i === 0 ? "D" : i === parts.length - 1 ? "A" : "C";
    for (let j = 0; j < parts[i]; j++) labels.push(ruolo);
  }
  // Pad fino a 11 in caso di moduli con 5+ parti (4-1-4-1, 4-2-3-1, ecc.)
  while (labels.length < 11) labels.push("C");
  return labels.slice(0, 11);
}
